import { collections } from '@modules/firebase';
import { toStripeSubscription, toUser, toUserTags } from '@modules/transformers';
import { assertIsType, assertIsTypedArray } from '@modules/types';
import { doc, getDoc, getDocs, onSnapshot, query, where } from 'firebase/firestore';
import type { StripeSubscription, User, UserTags } from 'types';
import type { DBStripeSubscription } from 'types/db';

import { isDBUser, isDBUserTags } from './predicates/user-predicates';

/**
 * Gets the users details
 * @param userId The ID of the user
 */
export async function getUser(userId: string): Promise<User | undefined> {
  const userSnapshot = await getDoc(doc(collections.users(), userId));

  // We could be operating as an anonymous user so there will be no corresponding entry in the DB
  if (!userSnapshot.exists()) return;

  const user = userSnapshot.data();
  assertIsType(user, isDBUser);

  return toUser(user);
}

interface UserDetails {
  user: User | undefined;
  userTags: UserTags | undefined;
  userSubscriptions: StripeSubscription[];
}

export async function getUserDetails(userId: string): Promise<UserDetails> {
  const [user, userTags, userSubscriptions] = await Promise.all([
    getUser(userId),
    getUserTags(userId),
    getUserSubscriptions(userId),
  ]);

  return { user, userTags, userSubscriptions };
}

export async function getUserSubscriptions(userId: string): Promise<StripeSubscription[]> {
  const subscriptionsCollectionRef = collections.userSubscriptions(userId);

  const snapshot = await getDocs(subscriptionsCollectionRef);
  const dbSubscriptions = snapshot.docs.map((doc) => doc.data()) as DBStripeSubscription[];
  const subscriptions = dbSubscriptions.map((subscription) => toStripeSubscription(subscription));

  return subscriptions;
}

export function subscribeToUserSubscriptions(
  userId: string,
  onUpdate: (subscriptions: StripeSubscription[]) => void,
): () => void {
  const subscriptionsCollectionRef = collections.userSubscriptions(userId);

  const unsubscribe = onSnapshot(subscriptionsCollectionRef, (subscriptionsSnapshot) => {
    const dbSubscriptions = subscriptionsSnapshot.docs.map((doc) => doc.data()) as DBStripeSubscription[];
    const subscriptions = dbSubscriptions.map((subscription) => toStripeSubscription(subscription));

    onUpdate(subscriptions);
  });

  return unsubscribe;
}

export function subscribeToUser(userId: string, onUpdate: (user: User | undefined) => void): () => void {
  const userDocRef = doc(collections.users(), userId);

  const unsubscribe = onSnapshot(userDocRef, (userSnapshot) => {
    if (!userSnapshot.exists()) {
      onUpdate(undefined);
      return;
    }

    const dbMobileUser = userSnapshot.data();

    assertIsType(dbMobileUser, isDBUser);

    onUpdate(toUser(dbMobileUser));
  });

  return unsubscribe;
}

export async function getAllWorkspaceUsers(orgId: string): Promise<User[]> {
  const docsQuery = query(collections.users(), where('org_ids', 'array-contains', orgId));

  const snapshot = await getDocs(docsQuery);
  const users = snapshot.docs.map((doc) => doc.data());
  assertIsTypedArray(users, isDBUser);

  return users.map((user) => toUser(user));
}

export async function getUserTags(userId: string): Promise<UserTags | undefined> {
  const snapshot = await getDoc(doc(collections.userTags(), userId));

  if (snapshot.exists()) {
    const dbUserTags = snapshot.data();
    assertIsType(dbUserTags, isDBUserTags);

    return toUserTags(dbUserTags);
  }
}

export function subscribeToUserTags(
  userId: string,
  onUpdate: (userTags: UserTags | undefined) => void,
): () => void {
  const docRef = doc(collections.userTags(), userId);

  const unsubscribe = onSnapshot(docRef, (snapshot) => {
    if (!snapshot.exists()) {
      onUpdate(undefined);
      return;
    }

    const dbUserTags = snapshot.data();

    assertIsType(dbUserTags, isDBUserTags);

    onUpdate(toUserTags(dbUserTags));
  });

  return unsubscribe;
}
